import { __assign } from '../../../../../../../node_modules/tslib/tslib.es6.js';
import React__default, { useRef, useReducer, useLayoutEffect, useCallback } from 'react';
import { VictoryChart, VictoryVoronoiContainer, VictoryAxis, VictoryLabel, VictoryLine, VictoryBar, VictoryArea } from 'victory';
import { useAxisStyle, ChartLayoutLarge } from '../styles.js';
import { useChartData } from '../hooks.js';
import { useTheme } from '@mui/material';
import { ChartTooltip } from './atoms.js';
import { useChartColor, useTicks } from './util.js';

var ZigChart = function (_a) {
    var _b, _c;
    var id = _a.id, data = _a.data, yAxisFormatter = _a.yAxisFormatter, tooltipFormatter = _a.tooltipFormatter, events = _a.events, bars = _a.bars, _d = _a.tickCount, tickCount = _d === void 0 ? 7 : _d, onlyIntegerTicks = _a.onlyIntegerTicks, _e = _a.showCurrentLine, showCurrentLine = _e === void 0 ? !bars : _e, _f = _a.chartProps, chartProps = _f === void 0 ? {} : _f, _g = _a.precision, precision = _g === void 0 ? 2 : _g;
    var theme = useTheme();
    var axisStyle = useAxisStyle();
    var _h = useChartData(data, "full", precision), processedData = _h.data, color = _h.color, gradient = _h.gradient, yDomain = _h.yDomain;
    var wrapperRef = useRef(null);
    var _j = ((_b = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) || { width: 0, height: 0 }, width = _j.width, height = _j.height;
    var pureChartWidth = width ? width - 70 - 2 : 0;
    var chartColors = useChartColor();
    var barChartWidth = pureChartWidth / processedData.length;
    var barChartWidthAdjustedForPadding = Math.min(25, pureChartWidth / (processedData.length + 2));
    var _k = useReducer(function (x) { return x + 1; }, 0), forceUpdate = _k[1];
    useLayoutEffect(function () {
        forceUpdate();
    }, [width]);
    var getChartLabel = useCallback(function (_a) {
        var _b = _a.datum, datum = _b === void 0 ? 0 : _b;
        return yAxisFormatter ? yAxisFormatter(datum) : datum.toString();
    }, [yAxisFormatter]);
    var getChartTooltip = useCallback(function (_a) {
        var datum = _a.datum;
        return tooltipFormatter ? tooltipFormatter(datum) : "".concat(datum.x, "\n").concat(datum.y);
    }, [tooltipFormatter]);
    var lastDataY = (_c = processedData[processedData.length - 1]) === null || _c === void 0 ? void 0 : _c.y;
    var ticks = useTicks(processedData, yDomain, tickCount, onlyIntegerTicks, showCurrentLine, height);
    return (React__default.createElement(ChartLayoutLarge, { ref: wrapperRef, id: id }, width && (React__default.createElement(VictoryChart, __assign({ containerComponent: React__default.createElement(VictoryVoronoiContainer, { voronoiDimension: "x", labels: getChartTooltip, labelComponent: React__default.createElement(ChartTooltip, { color: !bars ? color : undefined }), voronoiBlacklist: ["eventLine", "currentLine"] }) }, {
        domain: { y: yDomain },
        width: width || 600,
        height: 300,
        domainPadding: { x: bars ? [barChartWidth / 2, barChartWidth / 2] : 0, y: 1 },
        padding: { left: 35, top: 20, right: 35, bottom: 20 },
    }, chartProps),
        React__default.createElement(VictoryAxis, { tickValues: ticks, tickLabelComponent: React__default.createElement(VictoryLabel, { style: __assign(__assign({}, axisStyle.tickLabels), { fill: function (_a) {
                        var datum = _a.datum;
                        return showCurrentLine && datum === lastDataY
                            ? "".concat(color, "cc")
                            : theme.palette.neutral200;
                    } }), textAnchor: "start", text: getChartLabel }), dependentAxis: true, orientation: "right", style: axisStyle }),
        (events || []).map(function (_a) {
            var x = _a.x, label = _a.label;
            return (React__default.createElement(VictoryLine, { name: "eventLine", key: "event-line-" + x, style: {
                    data: { stroke: theme.palette.neutral500, strokeWidth: 0.5 },
                }, data: [
                    { x: x, y: yDomain[1] },
                    { x: x, y: yDomain[0] },
                ], labels: [label], labelComponent: React__default.createElement(VictoryLabel, { dy: 17, labelPlacement: "vertical", style: [{ fontSize: 14, fill: theme.palette.neutral500 }], angle: -90, textAnchor: "end" }) }));
        }),
        showCurrentLine && (React__default.createElement(VictoryLine, { name: "currentLine", key: "current-line", style: {
                data: {
                    stroke: "".concat(color, "80"),
                    strokeWidth: 1,
                    strokeDasharray: "1.75, 0.25",
                    background: theme.palette.neutral600,
                },
            }, data: [
                { x: 0, y: lastDataY },
                {
                    x: processedData.length,
                    y: lastDataY,
                },
            ] })),
        React__default.createElement(VictoryAxis, { tickFormat: function () { return ""; }, style: axisStyle }),
        React__default.createElement(VictoryAxis, { offsetY: 20, tickLabelComponent: React__default.createElement(VictoryLabel, { backgroundPadding: { top: 5 } }), fixLabelOverlap: true, style: bars
                ? __assign(__assign({}, axisStyle), { grid: { stroke: theme.palette.neutral700, strokeDasharray: "3 3" }, ticks: { stroke: theme.palette.neutral700, size: 5 } }) : axisStyle }),
        bars ? (React__default.createElement(VictoryBar, { barRatio: 0.9, animate: {
                duration: 2000,
                onLoad: { duration: 1000 },
            }, barWidth: barChartWidthAdjustedForPadding, style: {
                data: {
                    fill: function (_a) {
                        var datum = _a.datum;
                        return datum.y > 0 ? chartColors.green : chartColors.red;
                    },
                    maxWidth: 20,
                    strokeWidth: 0,
                },
            }, data: processedData.map(function (v) { return (__assign(__assign({}, v), { y0: 0 })); }) })) : (React__default.createElement(VictoryArea, { style: {
                data: {
                    fill: "url(#".concat(gradient, ")"),
                    strokeWidth: 2,
                    stroke: color,
                },
            }, data: processedData, interpolation: "monotoneX" }))))));
};

export { ZigChart as default };
